<script setup lang="ts">
import type { GeneralAddress } from 'src/types/componentsQuasar';
import { startCase } from 'shared/utilities/string';
import { getAddress } from './useGetAddress';
import {
  mdiHomeMapMarker,
  mdiCardAccountPhone,
  mdiEmail,
  mdiAccountEdit,
} from '@quasar/extras/mdi-v7';
import { useUserSettings } from '../../useUserSettings';
import type { User } from 'src/modules/User/models/User';

const user = defineModel<Partial<User>>();
const { address, phone } = useUserSettings(user);

const emit = defineEmits<{
  editProfile: []; // named tuple syntax
}>();
</script>

<template>
  <q-list>
    <q-item>
      <q-item-section
        avatar
        class="text-center"
      >
        <q-avatar
          size="xl"
          font-size="1.5rem"
          color="primary"
          style="text-transform: uppercase"
        >
          {{ user?.firstname?.charAt(0).toUpperCase()
          }}{{ user?.lastname?.charAt(0).toUpperCase() }}
        </q-avatar>
      </q-item-section>

      <q-item-section>
        <q-item-label>{{
          startCase(`${user?.firstname ?? ''} ${user?.lastname ?? ''}`)
        }}</q-item-label>
        <q-item-label
          caption
          class="text-grey-6"
        >
          {{ user?.email }}
        </q-item-label>
      </q-item-section>

      <q-item-section side>
        <q-btn
          color="primary"
          round
          flat
          :icon="mdiAccountEdit"
          @click="emit('editProfile')"
        />
      </q-item-section>
    </q-item>

    <q-separator />

    <q-item class="q-mt-lg">
      <q-item-section
        avatar
        class="items-center"
      >
        <q-btn
          :href="getAddress(address as GeneralAddress)"
          target="_blank"
          flat
          round
          outline
          no-caps
        >
          <q-icon :name="mdiHomeMapMarker" />
        </q-btn>
      </q-item-section>

      <q-item-section>
        <q-item-label>{{
          `${address?.number ?? ''} ${address?.street && address.street.length ? address?.street + ',' : ''}`
        }}</q-item-label>
        <q-item-label>{{
          `${address?.city && address.city.length ? address.city + ', ' : ''} ${address?.zip ?? ''}`
        }}</q-item-label>
        <q-item-label>{{ `${address?.country ?? ''}` }}</q-item-label>
      </q-item-section>
    </q-item>

    <q-item>
      <q-item-section
        avatar
        class="items-center"
      >
        <q-btn
          :href="`tel:${phone?.number}`"
          target="_blank"
          flat
          round
          outline
          no-caps
        >
          <q-icon :name="mdiCardAccountPhone" />
        </q-btn>
      </q-item-section>

      <q-item-section>
        <q-item-label>{{ `${phone?.number ?? ''}` }}</q-item-label>
      </q-item-section>
    </q-item>

    <q-item>
      <q-item-section
        avatar
        class="items-center"
      >
        <q-btn
          :href="`mailto:${user?.email}?subject=Flash Studies`"
          target="_blank"
          flat
          round
          outline
          no-caps
        >
          <q-icon :name="mdiEmail" />
        </q-btn>
      </q-item-section>

      <q-item-section>
        <q-item-label>{{ `${user?.email ?? ''}` }}</q-item-label>
      </q-item-section>
    </q-item>
  </q-list>
</template>
